import React, { useState, useEffect } from 'react';
import {
  Card,
  Table,
  Pagination,
  Button,
  Space,
  DatePicker,
  Avatar,
  Typography,
  Row,
  Col,
  Switch,
  Divider,
  Select,
} from 'antd';
import {
  DownloadOutlined,
  FilterOutlined,
  UserOutlined,
  CalendarOutlined,
  ReloadOutlined,
  BookOutlined,
} from '@ant-design/icons';
import dayjs from 'dayjs';
import useLearnerActivitiesReport from '../Hooks/Reports/useLearnerActivities';
import {
  LearnerActivityReportTypeEnum,
  LearnerInactivityPeriodEnum,
  SortDirectionEnum,
} from 'Common/Constants/Enums/Individual/Reports/LearnerActivityEnum';
import { getLocationString } from 'Common/Components/LocationHelper';


const { RangePicker } = DatePicker;
const { Title, Text } = Typography;
const { Option } = Select;

export default function LearnerActivitiesReport() {
  const {
    data,
    loading,
    pageNumber,
    pageSize,
    totalCount,
    filters,
    setFilters,
    setPageNumber,
    setPageSize,
    fetchReport,
    downloadReport,
  } = useLearnerActivitiesReport();

  // Local state for form inputs before applying
  const [localFilters, setLocalFilters] = useState({
    ...filters,
    FromDate: null,
    ToDate: null,
  });


  useEffect(() => {
    setLocalFilters({
      ...filters,
      FromDate: localFilters.FromDate,
      ToDate: localFilters.ToDate,
    });
  }, [filters]);


  useEffect(() => {
    const params = {
      PageNumber: pageNumber,
      PageSize: pageSize,
      ReportType: filters.ReportType,
      SortBy: filters.SortBy,
    };

    if (filters.FromDate) {
      params.FromDate = dayjs(filters.FromDate).format('YYYY-MM-DD');
    }
    if (filters.ToDate) {
      params.ToDate = dayjs(filters.ToDate).endOf('day').format('YYYY-MM-DD');
    }
    if (filters.InactiveSince) {
      params.InactiveSince = filters.InactiveSince;
    }

    fetchReport(params);
  }, [pageNumber, pageSize]); // Only re-run when pagination changes.

  // --- HANDLERS ---

  const handleApply = () => {

    setPageNumber(1);

    setFilters(localFilters);


    const params = {
      PageNumber: 1,
      PageSize: pageSize,
      ReportType: localFilters.ReportType,
      SortBy: localFilters.SortBy,
    };

    if (localFilters.FromDate) {
      params.FromDate = dayjs(localFilters.FromDate).format('YYYY-MM-DD');
    }
    if (localFilters.ToDate) {
      params.ToDate = dayjs(localFilters.ToDate).endOf('day').format('YYYY-MM-DD');
    }
    if (localFilters.InactiveSince) {
      params.InactiveSince = localFilters.InactiveSince;
    }


    fetchReport(params);
  };

  const handleReset = () => {
    // 1. Reset local filters to defaults
    const defaultFilters = {
      ReportType: LearnerActivityReportTypeEnum.ActiveLearners,
      SortBy: SortDirectionEnum.Ascending,
      FromDate: null,
      ToDate: null,
    };
    setLocalFilters(defaultFilters);

    // 2. Reset pagination and fetch with default filters
    setPageNumber(1);
    const params = {
      PageNumber: 1,
      PageSize: pageSize,
      ReportType: defaultFilters.ReportType,
      SortBy: defaultFilters.SortBy,
    };
    fetchReport(params);
  };

  const handleDownload = () => {
    const params = {
      ReportType: filters.ReportType,
      SortBy: filters.SortBy,
    };

    if (filters.FromDate) {
      params.FromDate = dayjs(filters.FromDate).format('YYYY-MM-DD');
    }
    if (filters.ToDate) {
      params.ToDate = dayjs(filters.ToDate).endOf('day').format('YYYY-MM-DD');
    }
    if (filters.InactiveSince) {
      params.InactiveSince = filters.InactiveSince;
    }

    downloadReport(params);
  };

  const handlePaginationChange = (p, s) => {
    setPageNumber(p);
    setPageSize(s);
    // The useEffect will automatically trigger the fetch with the new page/size
  };

  const columns = [
    {
      title: 'Learner',
      fixed: 'left',
      render: (_, r) => (
        <Space>
          <Avatar src={r.profileImage} icon={<UserOutlined />} />
          <div>
            <div>{r.firstName} {r.lastName}</div>
            <Text type="secondary">{r.learnerCode}</Text>
          </div>
        </Space>
      ),
    },
    { title: 'Email', dataIndex: 'email' },
    {
  title: 'Location',
  render: (_, record) => (
    getLocationString(record) || 'N/A'
  ),
},

    {
      title: 'Last Activity',
      dataIndex: 'lastActivity',
      render: d => (
        <Space>
          <CalendarOutlined />
          {dayjs(d).format('DD MMM YYYY, HH:mm')}
        </Space>
      ),
    },
    {
      title: 'Joined',
      dataIndex: 'createdOn',
      render: d => dayjs(d).format('DD MMM YYYY'),
    },

    {
      title: (
        <Space>
          <BookOutlined />
          Enrollments
        </Space>
      ),
      key: 'enrollments',
      render: (_, record) => (
        <Space direction="vertical" size="small">
          {record.enrolledCount}
        </Space>
      ),
    },
  ];

  return (
    <div style={{ maxWidth: 1400, margin: '0 auto', padding: 24 }}>
      <div style={{
        marginBottom: 24,
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'flex-start',
      }}>
        <div>
          <Title level={2} style={{ marginBottom: 4 }}>Learner Activities Report</Title>

          <Text type="secondary">Tracking learner activity and engagement</Text>
        </div>
        <Space>
          <Button
            onClick={() => window.history.back()} // or navigate('/') if using react-router
          >
            Back
          </Button>

          <Button
            type="primary"
            icon={<DownloadOutlined />}
            onClick={handleDownload}
          >
            Download
          </Button>
        </Space>

      </div>
      <Card
        style={{ marginTop: 16 }}
        title={<Space><FilterOutlined />Filters</Space>}

      >
        <Row gutter={16}>
          <Col md={8}>
            <Text strong>Report Type</Text>
            <Select
              style={{ width: '100%', marginTop: 6 }}
              value={localFilters.ReportType}
              onChange={(value) => setLocalFilters({
                ...localFilters,
                ReportType: value,
                // Reset InactiveSince when changing report type
                InactiveSince: value === LearnerActivityReportTypeEnum.InactiveLearners
                  ? LearnerInactivityPeriodEnum.Last7Days
                  : undefined,
              })}
            >
              <Option value={LearnerActivityReportTypeEnum.ActiveLearners}>Active Learners</Option>
              <Option value={LearnerActivityReportTypeEnum.InactiveLearners}>Inactive Learners</Option>
              <Option value={LearnerActivityReportTypeEnum.EnrolledLearners}>Enrolled Learners</Option>
            </Select>
          </Col>

          {localFilters.ReportType === LearnerActivityReportTypeEnum.InactiveLearners && (
            <Col md={8}>
              <Text strong>Inactivity Period</Text>
              <Select
                style={{ width: '100%', marginTop: 6 }}
                value={localFilters.InactiveSince || LearnerInactivityPeriodEnum.Last7Days}
                onChange={(value) => setLocalFilters({
                  ...localFilters,
                  InactiveSince: value,
                })}
              >
                <Option value={LearnerInactivityPeriodEnum.Last7Days}>Last 7 Days</Option>
                <Option value={LearnerInactivityPeriodEnum.Last30Days}>Last 30 Days</Option>
                <Option value={LearnerInactivityPeriodEnum.Last60Days}>Last 60 Days</Option>
                <Option value={LearnerInactivityPeriodEnum.Last90Days}>Last 90 Days</Option>
              </Select>
            </Col>
          )}

          <Col md={8}>
            <Text strong>Registered In Between Date Range</Text>
            <RangePicker
              style={{ width: '100%', marginTop: 6 }}
              value={
                localFilters.FromDate && localFilters.ToDate
                  ? [dayjs(localFilters.FromDate), dayjs(localFilters.ToDate)]
                  : null
              }
              onChange={(d) =>
                setLocalFilters({
                  ...localFilters,
                  FromDate: d?.[0]?.toDate() || null,
                  ToDate: d?.[1]?.toDate() || null,
                })
              }
            />
          </Col>

          <Col md={8}>
            <Text strong>Sort Direction</Text>
            <div style={{ marginTop: 6 }}>
              <Switch
                checked={localFilters.SortBy === SortDirectionEnum.Descending}
                onChange={(checked) => setLocalFilters({
                  ...localFilters,
                  SortBy: checked ? SortDirectionEnum.Descending : SortDirectionEnum.Ascending,
                })}
                checkedChildren="Descending"
                unCheckedChildren="Ascending"
              />
            </div>
          </Col>
        </Row>

        {/* ✅ New Section for Buttons at the bottom */}
        <Divider style={{ marginTop: 24, marginBottom: 16 }} />
        <Space style={{ width: '100%', justifyContent: 'flex-end' }}>
          <Button icon={<ReloadOutlined />} onClick={handleReset}>
            Reset
          </Button>
          <Button type="primary" onClick={handleApply}>
            Apply
          </Button>
        </Space>
      </Card>

      <Card style={{ marginTop: 16 }}>
        <Table
          rowKey="id"
          columns={columns}
          dataSource={data}
          loading={loading}
          pagination={false}
          scroll={{ x: 1200 }}
        />

        <Divider />

        <Pagination
          current={pageNumber}
          pageSize={pageSize}
          total={totalCount}
          showSizeChanger
          onChange={handlePaginationChange}
        />
      </Card>
    </div>
  );
}